/**
 * R-62/R-63/R-64 — orchestrates keyword, embedding, and LLM-judge drift checks.
 */
import type { ReportTypeId } from '../../config/reportTypes';
import type { SiteRagReport } from '../../types/siteRag';
import { AUDIT_CONFIG } from '../../config/auditConfig';
import { embedText } from '../embeddings/embedText';
import { logAuditEvent } from '../telemetry/auditTelemetry';
import { extractSections, flattenToText, reportFullText } from './reportTextExtract';
import { detectKeywordDrift, type KeywordDriftFinding } from './keywordDriftDetector';
import { detectEmbeddingDrift, type EmbedFn, type EmbeddingDriftFinding } from './embeddingDriftDetector';
import { detectLlmJudgeDrift, type LlmJudgeFn, type LlmJudgeDriftFinding } from './llmJudgeDriftDetector';
import { getRegistryEntry, getRegistryEntriesForType } from './sectionDriftRegistry';
import { verifyCompositeSectionStructure } from './compositeSectionVerifier';

export type SectionDriftDetector = 'keyword' | 'embedding' | 'llm-judge';

export type SectionDriftFinding =
  | KeywordDriftFinding
  | EmbeddingDriftFinding
  | LlmJudgeDriftFinding;

export interface VerifySectionDriftOptions {
  useLlmJudge?: boolean;
  embedFn?: EmbedFn;
  judgeFn?: LlmJudgeFn;
  llmJudgeMaxPerRun?: number;
}

export interface SectionDriftGateOptions extends VerifySectionDriftOptions {
  reportId?: string;
  siteId?: string;
}

function splitMarkdownByH2(markdown: string): Array<{ id: string; content: string }> {
  const sections: Array<{ id: string; content: string }> = [];
  const parts = markdown.split(/^##\s+/m);
  if (parts.length <= 1) return sections;
  for (let i = 1; i < parts.length; i += 1) {
    const chunk = parts[i];
    const nl = chunk.indexOf('\n');
    const heading = (nl >= 0 ? chunk.slice(0, nl) : chunk).trim();
    const content = nl >= 0 ? chunk.slice(nl + 1) : '';
    const id = heading
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '')
      .slice(0, 64) || `h2-${i}`;
    sections.push({ id, content: content.trim() });
  }
  return sections;
}

function resolveReportSections(report: SiteRagReport): Array<{ id: string; content: string }> {
  const reportTypeId = (report.reportType ?? 'feasibility') as ReportTypeId;
  const r = report as unknown as Record<string, unknown>;
  const registrySections = getRegistryEntriesForType(reportTypeId);
  const byRegistry: Array<{ id: string; content: string }> = [];

  for (const entry of registrySections) {
    let val = r[entry.sectionId];
    if (entry.sectionId.startsWith('customBrief') && report.customBriefAnswers?.length) {
      val = report.customBriefAnswers
        .map((a) => [a.question, a.findings, a.evidence].filter(Boolean).join('\n'))
        .join('\n\n');
    }
    if (typeof val === 'string' && val.trim()) {
      byRegistry.push({ id: entry.sectionId, content: val });
    } else if (val != null && typeof val === 'object') {
      const flat = flattenToText(val);
      if (flat.trim()) byRegistry.push({ id: entry.sectionId, content: flat });
    }
  }

  const fieldSections = extractSections(report);
  const md = (report as SiteRagReport & { markdown?: string }).markdown?.trim() ?? '';
  if (md.length > 100) {
    const h2Sections = splitMarkdownByH2(md);
    if (h2Sections.length >= byRegistry.length && h2Sections.length >= fieldSections.length) {
      return h2Sections;
    }
  }
  if (byRegistry.length > 0) return byRegistry;
  if (fieldSections.length > 0) return fieldSections;
  if (md.length > 0) return [{ id: 'markdown', content: md }];
  return [{ id: 'fullReport', content: reportFullText(report) }];
}

function isWarnOrBlock(f: SectionDriftFinding): boolean {
  return f.severity === 'block' || f.severity === 'warn';
}

function sectionHasWarnPlus(findings: SectionDriftFinding[], sectionId: string): boolean {
  return findings.some((f) => f.sectionId === sectionId && isWarnOrBlock(f));
}

async function verifyOneSection(
  sectionId: string,
  content: string,
  reportTypeId: ReportTypeId,
  embedFn: EmbedFn,
): Promise<SectionDriftFinding[]> {
  const entry = getRegistryEntry(reportTypeId, sectionId);
  if (!entry || !content.trim()) return [];

  const [keywordFindings, embeddingFindings] = await Promise.all([
    Promise.resolve(detectKeywordDrift(content, entry)),
    detectEmbeddingDrift(content, entry, embedFn),
  ]);

  return [...keywordFindings, ...embeddingFindings];
}

export async function verifySectionDrift(
  report: SiteRagReport,
  opts: VerifySectionDriftOptions = {},
): Promise<SectionDriftFinding[]> {
  const reportTypeId = (report.reportType ?? 'feasibility') as ReportTypeId;
  const embedFn = opts.embedFn ?? embedText;
  const sections = resolveReportSections(report);
  const registrySections = new Set(getRegistryEntriesForType(reportTypeId).map((e) => e.sectionId));

  const targets = sections.filter((s) => registrySections.has(s.id) && s.content.trim().length >= 40);
  const worker = targets.length > 5
    ? (items: typeof targets) => Promise.all(items.map((s) => verifyOneSection(s.id, s.content, reportTypeId, embedFn)))
    : (items: typeof targets) => Promise.all(items.map((s) => verifyOneSection(s.id, s.content, reportTypeId, embedFn)));

  const nested = await worker(targets);
  let findings = nested.flat();

  const useLlm = opts.useLlmJudge
    && AUDIT_CONFIG.ENABLE_LLM_JUDGE;
  if (useLlm) {
    const cap = opts.llmJudgeMaxPerRun ?? AUDIT_CONFIG.LLM_JUDGE_MAX_PER_RUN;
    const judgeFn = opts.judgeFn;
    const candidates = targets.filter((s) => sectionHasWarnPlus(findings, s.id));
    let llmUsed = 0;
    for (const section of candidates) {
      if (llmUsed >= cap) break;
      const entry = getRegistryEntry(reportTypeId, section.id);
      if (!entry) continue;
      const llmFindings = await detectLlmJudgeDrift(section.content, entry, judgeFn);
      if (llmFindings.length > 0) llmUsed += 1;
      findings = [...findings, ...llmFindings];
    }
  }

  // DAF-19 — composite rollup structural checks (SECTION_SCHEMAS).
  findings = [...findings, ...verifyCompositeSectionStructure(report)];

  return findings;
}

export async function applySectionDriftGate(
  report: SiteRagReport,
  opts: SectionDriftGateOptions = {},
): Promise<SiteRagReport> {
  const findings = await verifySectionDrift(report, opts);
  if (findings.length === 0) return report;

  const blocks = findings.filter((f) => f.severity === 'block');
  if (blocks.length > 0) {
    console.info('[R-62/R-64 section drift] Blocking findings:', {
      reportId: report.id,
      count: blocks.length,
      sections: [...new Set(blocks.map((f) => f.sectionId))],
    });
  }

  logAuditEvent({
    type: 'section-drift-detected',
    siteId: opts.siteId ?? report.siteId,
    reportId: opts.reportId ?? report.id,
    reportType: report.reportType,
    payload: {
      findingCount: findings.length,
      blockCount: blocks.length,
      detectors: [...new Set(findings.map((f) => f.detector))],
    },
  });

  return {
    ...report,
    qaFindings: {
      ...(report.qaFindings ?? {}),
      sectionDriftFindings: findings.map((f) => ({
        detector: f.detector,
        reportTypeId: f.reportTypeId,
        sectionId: f.sectionId,
        severity: f.severity,
        message: f.message,
        ...(f.detector === 'keyword'
          ? {
            overlap: f.overlap,
            matchedKeywords: f.matchedKeywords,
            missingKeywords: f.missingKeywords,
            prohibitedHits: f.prohibitedHits,
          }
          : {}),
        ...(f.detector === 'embedding' ? { similarity: f.similarity } : {}),
        ...(f.detector === 'llm-judge' ? { score: f.score, rationale: f.rationale } : {}),
      })),
    },
  };
}
